import * as THREE from 'three';
import { isSpellAttack, spellEffects, type SpellAttackId } from '../../../content/spells/effects.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { UnitAnimator } from '../entities/unit-animator.js';
import type { CastState } from '../../../shared/game/class-types.js';

const UP = new THREE.Vector3(0, 1, 0), TAU = Math.PI * 2;
/** Mockery's arrival keeps its violet to the rim, where the flight's pale pink reads as a grey ring over the target. */
const MOCKERY_IMPACT_ACCENT = 0xd3a6f5;
const additive = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide } as const;
const VERTEX = `
  uniform float uTime; uniform float uWarp;
  varying vec2 vUv; varying vec3 vNormal; varying vec3 vView;
  void main() {
    vUv = uv;
    float ripple = sin(position.x * 19.0 + position.z * 13.0 - uTime * 18.0)
      * sin(position.y * 11.0 + uTime * 7.0);
    vec3 p = position + normal * ripple * uWarp;
    vec4 view = modelViewMatrix * vec4(p, 1.0);
    vNormal = normalize(normalMatrix * normal); vView = -view.xyz;
    gl_Position = projectionMatrix * view;
  }`;
const FRAGMENT = `
  uniform float uTime; uniform float uOpacity; uniform float uKind;
  uniform vec3 uColor; uniform vec3 uAccent;
  varying vec2 vUv; varying vec3 vNormal; varying vec3 vView;
  void main() {
    float rim = pow(1.0 - abs(dot(normalize(vNormal), normalize(vView))), 2.4);
    float edge = pow(abs(vUv.y * 2.0 - 1.0), 8.0);
    float flow = sin(vUv.x * 55.0 - uTime * 21.0 + sin(vUv.y * 16.0 + uTime * 4.0) * 2.0);
    float filament = pow(max(0.0, flow), 18.0);
    float endFade = smoothstep(0.0, .06, vUv.x) * (1.0 - smoothstep(.9, 1.0, vUv.x));
    float surface = .035 + filament * .2 + rim * .12;
    float alpha = (surface + edge * .6) * endFade * uOpacity;
    if (uKind > .5) {
      float interference = pow(max(0.0, sin(vUv.y * 68.0 - uTime * 24.0 + flow * .8)), 14.0);
      alpha = (.025 + interference * .2 + rim * .32) * uOpacity;
    }
    gl_FragColor = vec4(mix(uColor, uAccent, clamp(edge + filament + rim * .4, 0.0, 1.0)), alpha);
  }`;
function surface(id: SpellAttackId | 'breath-of-life', kind = 0, warp = .012, accent?: number) {
  const palette = spellEffects[id];
  return new THREE.ShaderMaterial({ vertexShader: VERTEX, fragmentShader: FRAGMENT, ...additive,
    uniforms: { uTime: { value: 0 }, uOpacity: { value: 1 }, uKind: { value: kind }, uWarp: { value: warp },
      uColor: { value: new THREE.Color(palette.color) }, uAccent: { value: new THREE.Color(accent ?? palette.accent) } } });
}
function shade(group: THREE.Object3D, time: number, opacity: number) {
  group.traverse(node => {
    if (node instanceof THREE.Mesh || node instanceof THREE.Points) {
      const mat = node.material;
      if (mat instanceof THREE.ShaderMaterial) {
        mat.uniforms.uTime.value = time; mat.uniforms.uOpacity.value = opacity;
      } else if (mat instanceof THREE.Material) mat.opacity = opacity;
    }
  });
}
function dispose(group: THREE.Object3D) {
  group.removeFromParent();
  const geometry = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
  group.traverse(node => {
    if (node instanceof THREE.Mesh || node instanceof THREE.Points || node instanceof THREE.Line) {
      geometry.add(node.geometry); for (const mat of [node.material].flat()) materials.add(mat);
    }
  });
  geometry.forEach(value => value.dispose()); materials.forEach(value => value.dispose());
}
/** A tapered, cupped crescent with an actual 3D cross-section, rather than a torus arc. */
function crescent(radius: number) {
  const at: number[] = [], uv: number[] = [], indices: number[] = [];
  const along = 44, across = 6;
  for (let i = 0; i <= along; i++) {
    const t = i / along, angle = -.8 + t * 4.4, width = .13 * Math.pow(Math.sin(t * Math.PI), .7);
    for (let j = 0; j <= across; j++) {
      const s = j / across, r = radius - width * (1 - s);
      at.push(Math.cos(angle) * r, Math.sin(s * Math.PI) * width * .45, Math.sin(angle) * r); uv.push(t, s);
      if (i < along && j < across) { const a = i * (across + 1) + j, b = a + across + 1; indices.push(a, b, a + 1, b, b + 1, a + 1); }
    }
  }
  const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.Float32BufferAttribute(at, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2)); geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}
function pressureShell(radius: number) {
  // The open, distorted bowl is normal to the direction of travel (local +Y).
  const geometry = new THREE.SphereGeometry(radius, 40, 20, 0, TAU, .3, 1.35);
  return geometry;
}
function flightBody(id: SpellAttackId) {
  const group = new THREE.Group(); group.name = `${id}-flight`;
  if (id === 'razor-wind') {
    for (let i = 0; i < 2; i++) {
      const mesh = new THREE.Mesh(crescent(spellEffects[id].radius * (i ? .8 : 1)), surface(id));
      mesh.rotation.y = i * Math.PI; mesh.rotation.z = i ? .28 : -.18; mesh.position.y = i * -.12; group.add(mesh);
    }
  } else {
    for (let i = 0; i < 3; i++) {
      const mesh = new THREE.Mesh(pressureShell(spellEffects[id].radius * (1 - i * .2)), surface(id, 1, .035));
      mesh.position.y = -.17 * i; mesh.rotation.z = .1 * Math.sin(i * 2); group.add(mesh);
    }
  }
  return group;
}
class Wake {
  readonly group = new THREE.Group();
  private ribbons: THREE.Mesh[] = [];
  private particles: THREE.Points;
  private history: { p: number; point: THREE.Vector3 }[] = [];
  constructor(private id: SpellAttackId | 'breath-of-life', count: number) {
    this.group.name = `${id}-wake`;
    for (let strip = 0; strip < 2; strip++) {
      const at = new Float32Array(34 * 2 * 3), uv: number[] = [], indices: number[] = [];
      for (let i = 0; i < 34; i++) { uv.push(i / 33, 0, i / 33, 1); if (i < 33) { const a = i * 2; indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); } }
      const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(at, 3));
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2)); geometry.setIndex(indices); geometry.computeVertexNormals();
      const mesh = new THREE.Mesh(geometry, surface(id)); mesh.frustumCulled = false; this.ribbons.push(mesh); this.group.add(mesh);
    }
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    geometry.setAttribute('aLife', new THREE.BufferAttribute(new Float32Array(count), 1));
    const material = new THREE.ShaderMaterial({ ...additive,
      vertexShader: `attribute float aLife; uniform float uSize; varying float vLife; void main(){vLife=aLife;gl_PointSize=uSize*(.5+.5*aLife);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader: `uniform float uOpacity; uniform vec3 uColor; varying float vLife; void main(){float d=length(gl_PointCoord-.5)*2.0;gl_FragColor=vec4(uColor,pow(max(0.0,1.0-d),2.0)*vLife*uOpacity);}`,
      uniforms: { uSize: { value: 3 }, uTime: { value: 0 }, uOpacity: { value: 1 }, uColor: { value: new THREE.Color(spellEffects[id].accent) } } });
    this.particles = new THREE.Points(geometry, material); this.particles.frustumCulled = false; this.group.add(this.particles);
  }
  update(from: THREE.Vector3, to: THREE.Vector3, p: number, time: number, length: number, opacity: number, ppm: number) {
    const delta = new THREE.Vector3().subVectors(to, from), distance = Math.max(.01, delta.length()), axis = delta.clone().normalize();
    const side = new THREE.Vector3().crossVectors(axis, UP); if (side.lengthSq() < .001) side.set(1, 0, 0); side.normalize();
    const cross = new THREE.Vector3().crossVectors(axis, side).normalize();
    const center = new THREE.Vector3();
    if (!this.history.length) this.history.push({ p: 0, point: from.clone() });
    if (p > this.history.at(-1)!.p) this.history.push({ p, point: from.clone().lerp(to, p) });
    // Once emitted, the wake's centre stays in world space even if the target moves.
    const path = (progress: number, point: THREE.Vector3) => {
      const index = this.history.findIndex(sample => sample.p >= progress);
      if (index <= 0) return point.copy(this.history[index < 0 ? this.history.length - 1 : 0].point);
      const a = this.history[index - 1], b = this.history[index];
      return point.copy(a.point).lerp(b.point, (progress - a.p) / Math.max(.00001, b.p - a.p));
    };
    this.ribbons.forEach((mesh, strip) => {
      const at = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < 34; i++) {
        const t = i / 33, behind = (1 - t) * length, progress = Math.max(0, p - behind / distance);
        const phase = progress * distance * 8 - time * 12 + strip * Math.PI;
        const radius = .05 + (1 - t) * .15, width = .015 * Math.sin(t * Math.PI);
        path(progress, center).addScaledVector(side, Math.cos(phase) * radius).addScaledVector(cross, Math.sin(phase) * radius);
        for (let j = 0; j < 2; j++) { const point = center.clone().addScaledVector(cross, (j ? 1 : -1) * width); at.setXYZ(i * 2 + j, point.x, point.y, point.z); }
      }
      at.needsUpdate = true; mesh.geometry.computeVertexNormals();
    });
    const at = this.particles.geometry.getAttribute('position') as THREE.BufferAttribute, life = this.particles.geometry.getAttribute('aLife') as THREE.BufferAttribute;
    for (let i = 0; i < at.count; i++) {
      const age = (time * 2.3 + i * .618) % 1, behind = age * length, progress = Math.max(0, p - behind / distance);
      const phase = progress * distance * 8 + i * 2.399, radius = .03 + age * .14;
      path(progress, center).addScaledVector(side, Math.cos(phase) * radius).addScaledVector(cross, Math.sin(phase) * radius);
      at.setXYZ(i, center.x, center.y, center.z); life.setX(i, (1 - age) * Math.min(1, p * 10));
    }
    at.needsUpdate = true; life.needsUpdate = true;
    (this.particles.material as THREE.ShaderMaterial).uniforms.uSize.value = Math.max(1.5, .045 * ppm);
    shade(this.group, time, opacity);
  }
  dispose() { dispose(this.group); }
}
interface Flight { body: THREE.Group; wake: Wake; from: THREE.Vector3; to: THREE.Vector3; p: number; elapsed: number; duration: number; ended?: number; id: SpellAttackId }
interface Burst { group: THREE.Group; id: SpellAttackId | 'breath-of-life'; targetId: string; at: number; position: THREE.Vector3; height: number; duration: number; leaves?: THREE.Mesh[];
  /** A miss: the spell carries on past its target (Razor Wind) or breaks up short of it (Mockery), in world space along `along`. */
  miss?: { start: THREE.Vector3; along: THREE.Vector3; side: THREE.Vector3 } }
export interface SpellLight { position: THREE.Vector3; color: THREE.Color; output: number }

/** All spell motion uses the interpolated game clock: pausing freezes surfaces, wakes, leaves and Taunt. */
export class SpellEffects {
  get visualObjects(): THREE.Object3D[] { return [...this.flights.values()].flatMap(flight => [flight.body, flight.wake.group])
    .concat([...this.approach.values()].map(wake => wake.group), this.bursts.map(burst => burst.group), [...this.taunts.values()]); }
  private warmup = new THREE.Group();
  private flights = new Map<number, Flight>();
  private origins = new Map<string, THREE.Vector3>();
  private approach = new Map<string, Wake>();
  private bursts: Burst[] = [];
  private pending: { event: CombatVisualEvent; at: number }[] = [];
  private taunts = new Map<string, THREE.Group>();
  private previewId = -1;
  private previewShots = new Map<number, { event: CombatVisualEvent; origin: THREE.Vector3; at: number; duration: number }>();
  private previewTaunts = new Map<string, number>();
  /** Told when a previewed cantrip reaches its target, so the preview can be heard as well as seen. */
  onPreviewLand: (abilityId: string, at: { x: number; z: number }) => void = () => {};
  readonly lights: SpellLight[] = [];
  constructor(private scene: THREE.Scene, private heights: Map<string, number>, private groundHeight: (x: number, z: number) => number = () => 0) {
    this.warmup.name = 'spell-shader-warmup';
    this.warmup.add(flightBody('razor-wind'), flightBody('vicious-mockery'), new Wake('razor-wind', 1).group);
    this.warmup.add(new THREE.Mesh(new THREE.PlaneGeometry(.1, .1), new THREE.MeshStandardMaterial({ color: 0x79a985, roughness: .8, side: THREE.DoubleSide, transparent: true, depthWrite: false })));
  }
  prepare() { this.scene.add(this.warmup); }
  prepared() { this.warmup.removeFromParent(); }
  receive(event: CombatVisualEvent, at: number) {
    if ((event.kind === 'heal' && event.abilityId === 'breath-of-life') || ((event.kind === 'impact' || event.kind === 'miss') && isSpellAttack(event.attackId ?? ''))) this.pending.push({ event, at });
  }
  /** Authoring shots use the same flight, wake and arrival factories, without server damage or healing. */
  preview(event: CombatVisualEvent, origin: THREE.Vector3, at: number) {
    if (event.abilityId === 'breath-of-life') { this.receive({ ...event, kind: 'heal' }, at); return; }
    if (!isSpellAttack(event.abilityId ?? '')) return;
    const distance = Math.hypot(event.to.x - origin.x, event.to.z - origin.z);
    this.previewShots.set(this.previewId--, { event, origin: origin.clone(), at,
      duration: THREE.MathUtils.clamp(distance / (event.abilityId === 'razor-wind' ? 18 : 14), .22, .8) });
  }
  private burst(event: CombatVisualEvent, at: number) {
    const id = event.kind === 'heal' ? 'breath-of-life' : event.attackId as SpellAttackId;
    const group = new THREE.Group(); group.name = `${id}-impact`;
    const height = this.heights.get(event.targetId) ?? 2;
    const position = new THREE.Vector3(event.to.x, this.groundHeight(event.to.x, event.to.z) + (id === 'vicious-mockery' ? height * .84 : height * .52), event.to.z);
    const item: Burst = { group, id, targetId: event.targetId, at, position, height, duration: id === 'breath-of-life' ? spellEffects[id].duration : spellEffects[id].impact };
    if (event.kind === 'miss' && id !== 'breath-of-life') {
      const along = new THREE.Vector3(event.to.x - event.from.x, 0, event.to.z - event.from.z);
      if (along.lengthSq() < .0001) along.set(0, 0, 1); along.normalize();
      const side = new THREE.Vector3().crossVectors(along, UP).multiplyScalar(event.id % 2 ? 1 : -1);
      item.duration = spellEffects[id].miss;
      if (id === 'razor-wind') {
        // The blades slip past the target's flank and blow on, thinning out.
        item.miss = { start: position.clone().addScaledVector(along, -.25).addScaledVector(side, .3), along, side };
        for (let i = 0; i < 2; i++) { const mesh = new THREE.Mesh(crescent(.24 + i * .07), surface(id)); mesh.rotation.set(i * .5, i * 2.4, i * .4); group.add(mesh); }
      } else {
        // The jeer's pressure front stalls short of the face and splits apart.
        item.miss = { start: position.clone().addScaledVector(along, -.6), along, side };
        for (let i = 0; i < 2; i++) { const mesh = new THREE.Mesh(pressureShell(.3 + i * .1), surface(id, 1, .035, MOCKERY_IMPACT_ACCENT)); mesh.rotation.set(0, i * Math.PI, Math.PI / 2); group.add(mesh); }
      }
      group.position.copy(item.miss.start); this.scene.add(group); this.bursts.push(item); return;
    }
    if (id === 'breath-of-life') {
      // Broad, nearly invisible ribbons wrap the ally instead of an opaque cone.
      for (let i = 0; i < 3; i++) { const mesh = new THREE.Mesh(crescent(.52 + i * .03), surface(id, 0, .018)); mesh.rotation.y = i * TAU / 3; group.add(mesh); }
      const leafShape = new THREE.Shape(); leafShape.moveTo(0, -.1); leafShape.quadraticCurveTo(.09, 0, 0, .1); leafShape.quadraticCurveTo(-.06, 0, 0, -.1);
      const geometry = new THREE.ShapeGeometry(leafShape, 8); const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) pos.setZ(i, .025 * Math.sin((pos.getY(i) + .1) * Math.PI / .2)); geometry.computeVertexNormals();
      item.leaves = Array.from({ length: spellEffects[id].leaves }, (_, i) => {
        const material = new THREE.MeshStandardMaterial({ color: i % 3 === 0 ? 0xabc897 : 0x79a985, roughness: .8, side: THREE.DoubleSide, transparent: true, depthWrite: false });
        const leaf = new THREE.Mesh(geometry, material); leaf.name = 'healing-leaf'; leaf.scale.setScalar(.8 + (i % 3) * .17); group.add(leaf); return leaf;
      });
    } else if (id === 'razor-wind') {
      for (let i = 0; i < 3; i++) { const mesh = new THREE.Mesh(crescent(.28 + i * .1), surface(id)); mesh.rotation.set(i * .6, i * 2.1, i * .7); group.add(mesh); }
    } else {
      for (let i = 0; i < 2; i++) { const mesh = new THREE.Mesh(pressureShell(.32 + i * .11), surface(id, 1, .035, MOCKERY_IMPACT_ACCENT)); mesh.rotation.z = Math.PI / 2; group.add(mesh); }
    }
    group.position.copy(position); this.scene.add(group); this.bursts.push(item);
  }
  update(snapshot: WorldSnapshot, earlier: WorldSnapshot, alpha: number, positions: ReadonlyMap<string, THREE.Object3D>, ppm: number, animators: ReadonlyMap<string, UnitAnimator>, previewCasts: ReadonlyMap<string, CastState> = new Map()) {
    const time = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha; this.lights.length = 0;
    for (const [id, animator] of animators) { const origin = animator.spellOrigin?.(); if (origin && positions.get(id)?.visible) this.origins.set(id, origin.clone()); }
    for (const id of this.origins.keys()) if (!positions.get(id)?.visible) this.origins.delete(id);
    const shots = new Map(snapshot.projectiles.map(shot => [shot.id, shot]));
    // The earlier snapshot's departing shot still exists until the drawn interval reaches arrival.
    if (alpha < 1) for (const shot of earlier.projectiles) if (!shots.has(shot.id)) shots.set(shot.id, { ...shot, remaining: 0 });
    for (const [id, preview] of this.previewShots) {
      const target = positions.get(preview.event.targetId);
      if (!target?.visible) { this.previewShots.delete(id); continue; }
      const remaining = preview.duration - (time - preview.at);
      if (remaining <= 0) {
        const hit = preview.event.kind !== 'miss';
        this.burst({ ...preview.event, id, kind: hit ? 'impact' : 'miss', attackId: preview.event.abilityId, to: { x: target.position.x, z: target.position.z } }, preview.at + preview.duration);
        if (hit) this.onPreviewLand(preview.event.abilityId!, { x: target.position.x, z: target.position.z });
        if (hit && preview.event.abilityId === 'vicious-mockery') this.previewTaunts.set(preview.event.targetId, preview.at + preview.duration + 5);
        this.previewShots.delete(id); continue;
      }
      shots.set(id, { id, actorId: preview.event.actorId, targetId: preview.event.targetId, attackId: preview.event.abilityId!,
        from: { x: preview.origin.x, z: preview.origin.z }, to: { x: target.position.x, z: target.position.z }, duration: preview.duration, remaining });
    }
    const active = new Set(shots.keys());
    for (const shot of shots.values()) {
      if (!isSpellAttack(shot.attackId)) continue;
      const before = earlier.projectiles.find(item => item.id === shot.id);
      const remaining = this.previewShots.has(shot.id) ? shot.remaining : THREE.MathUtils.lerp(before?.remaining ?? shot.duration, shot.remaining, alpha);
      let flight = this.flights.get(shot.id);
      if (!flight) {
        const from = this.previewShots.get(shot.id)?.origin.clone() ?? this.origins.get(shot.actorId)?.clone() ?? new THREE.Vector3(shot.from.x, this.groundHeight(shot.from.x, shot.from.z) + (this.heights.get(shot.actorId) ?? 2) * .76, shot.from.z);
        flight = { body: flightBody(shot.attackId), wake: new Wake(shot.attackId, spellEffects[shot.attackId].particles), from, to: new THREE.Vector3(), p: 0, elapsed: 0, duration: shot.duration, id: shot.attackId };
        this.scene.add(flight.body, flight.wake.group); this.flights.set(shot.id, flight);
      }
      flight.to.set(THREE.MathUtils.lerp(before?.to.x ?? shot.to.x, shot.to.x, alpha), (this.heights.get(shot.targetId) ?? 2) * (shot.attackId === 'vicious-mockery' ? .84 : .52), THREE.MathUtils.lerp(before?.to.z ?? shot.to.z, shot.to.z, alpha));
      flight.to.y += this.groundHeight(flight.to.x, flight.to.z);
      flight.p = Math.max(0, Math.min(1, 1 - remaining / shot.duration)); flight.elapsed = shot.duration - remaining;
      flight.body.position.copy(flight.from).lerp(flight.to, flight.p);
      const tangent = new THREE.Vector3().subVectors(flight.to, flight.from).normalize(); if (tangent.lengthSq() < .001) tangent.set(0, 0, 1);
      flight.body.quaternion.setFromUnitVectors(UP, tangent);
      if (shot.attackId === 'razor-wind') flight.body.rotateY(flight.elapsed * 22);
      else flight.body.children.forEach((shell, i) => { shell.scale.setScalar(1 + Math.sin(flight.elapsed * 21 - i) * .1); });
      const born = THREE.MathUtils.smoothstep(flight.elapsed, 0, .06); flight.body.scale.setScalar(.45 + born * .55); shade(flight.body, flight.elapsed, born * .85);
      const length = Math.min(1.5, flight.from.distanceTo(flight.to) * flight.p);
      flight.wake.update(flight.from, flight.to, flight.p, flight.elapsed, length, born * .45, ppm);
      this.lights.push({ position: flight.body.position, color: new THREE.Color(spellEffects[shot.attackId].color), output: .1 * born });
    }
    for (const [id, flight] of this.flights) if (!active.has(id)) {
      if (flight.ended === undefined) { flight.ended = time; dispose(flight.body); }
      const p = (time - flight.ended) / spellEffects[flight.id].wake;
      if (p >= 1) { flight.wake.dispose(); this.flights.delete(id); }
      else flight.wake.update(flight.from, flight.to, 1, flight.duration + time - flight.ended, 1.2 * (1 - p), .3 * (1 - p), ppm);
    }
    const approaches = new Set<string>();
    for (const hero of snapshot.heroes) {
      const preview = previewCasts.get(hero.id);
      const old = earlier.heroes.find(unit => unit.id === hero.id)?.class.casting, next = hero.class.casting;
      const cast = preview ?? next ?? old; if (cast?.abilityId !== 'breath-of-life' || !cast.targetId || hero.health.dead || hero.health.dying) continue;
      const remaining = preview?.remaining ?? THREE.MathUtils.lerp(old?.remaining ?? cast.duration, next?.remaining ?? 0, alpha);
      if (remaining > spellEffects['breath-of-life'].approach || remaining <= 0) continue;
      const target = positions.get(cast.targetId); if (!target?.visible) continue;
      approaches.add(hero.id); let wake = this.approach.get(hero.id);
      if (!wake) { wake = new Wake('breath-of-life', 4); this.scene.add(wake.group); this.approach.set(hero.id, wake); }
      const from = this.origins.get(hero.id) ?? new THREE.Vector3(hero.x, this.groundHeight(hero.x, hero.z) + 1.3, hero.z);
      const to = target.position.clone(); to.y += (this.heights.get(cast.targetId) ?? 2) * .52;
      const p = 1 - remaining / spellEffects['breath-of-life'].approach;
      wake.update(from, to, p, p * .28, Math.min(1, from.distanceTo(to) * p), Math.sin(p * Math.PI) * .3, ppm);
    }
    for (const [id, wake] of this.approach) if (!approaches.has(id)) { wake.dispose(); this.approach.delete(id); }
    let waiting = 0;
    for (const item of this.pending) { if (time + .001 >= item.at) this.burst(item.event, item.at); else this.pending[waiting++] = item; } this.pending.length = waiting;
    this.bursts = this.bursts.filter(item => {
      const p = (time - item.at) / item.duration; if (p >= 1 || !positions.get(item.targetId)?.visible) { dispose(item.group); return false; }
      if (item.miss) {
        const { start, along, side } = item.miss, fade = Math.pow(1 - p, 1.6) * THREE.MathUtils.smoothstep(p, 0, .06);
        if (item.id === 'razor-wind') {
          item.group.position.copy(start).addScaledVector(along, 1.9 * (1 - Math.pow(1 - p, 2))).addScaledVector(side, .25 * p);
          item.group.position.y += .2 * p; item.group.scale.setScalar(.75 + p * .7); item.group.rotation.y = time * 9;
        } else {
          item.group.position.copy(start).addScaledVector(along, .2 * p);
          item.group.scale.setScalar(.9 + p * .5);
          item.group.children.forEach((shell, i) => { shell.position.copy(side).multiplyScalar((i ? 1 : -1) * .4 * p); shell.position.y = -.15 * p; });
        }
        shade(item.group, time - item.at, fade * 1.25); item.position.copy(item.group.position);
        this.lights.push({ position: item.position, color: new THREE.Color(spellEffects[item.id].color), output: fade * .08 }); return true;
      }
      const target = positions.get(item.targetId)!;
      item.position.x = target.position.x; item.position.z = target.position.z; item.group.position.copy(item.position);
      item.position.y = target.position.y + item.height * (item.id === 'vicious-mockery' ? .84 : .52); item.group.position.y = item.position.y;
      const fade = Math.pow(1 - p, 1.4) * THREE.MathUtils.smoothstep(p, 0, .05);
      if (item.id === 'breath-of-life') {
        item.group.position.y = target.position.y + .1;
        item.group.children.slice(0, 3).forEach((mesh, i) => { const t = Math.min(1, p * 1.1 + i * .14); mesh.position.y = .12 + t * item.height * .7; mesh.rotation.y = time * 2.6 + i * TAU / 3; mesh.scale.setScalar(.9 + Math.sin(p * Math.PI) * .2); });
        item.leaves?.forEach((leaf, i) => { const t = THREE.MathUtils.clamp(p * 1.3 - i * .035, 0, 1), angle = t * 8 + i * 2.399, height = .15 + t * item.height * .8, radius = .35 + Math.sin(i * 3.1 + p * 4) * .12; leaf.visible = t > 0; leaf.position.set(Math.cos(angle) * radius, height, Math.sin(angle) * radius); leaf.rotation.set(t * 9 + i, -angle, Math.sin(t * 12 + i) * .5); });
      } else { item.group.scale.setScalar(.5 + p * 1.6); item.group.rotation.y = p * (item.id === 'razor-wind' ? 6 : 1.2); }
      shade(item.group, time - item.at, fade * (item.id === 'breath-of-life' ? .48 : 1.25));
      this.lights.push({ position: item.position, color: new THREE.Color(spellEffects[item.id].color), output: fade * (item.id === 'breath-of-life' ? .12 : .2) }); return true;
    });
    const taunted = new Set<string>();
    for (const enemy of snapshot.enemies) {
      const previewUntil = this.previewTaunts.get(enemy.id), remaining = enemy.combat.taunt?.remaining ?? (previewUntil === undefined ? 0 : previewUntil - time);
      if (previewUntil !== undefined && previewUntil <= time) this.previewTaunts.delete(enemy.id);
      if (remaining <= 0 || enemy.health.dead || !positions.get(enemy.id)?.visible) continue;
      taunted.add(enemy.id); let group = this.taunts.get(enemy.id);
      if (!group) { group = flightBody('vicious-mockery'); group.name = 'mockery-taunt'; group.scale.setScalar(.6); this.scene.add(group); this.taunts.set(enemy.id, group); }
      const position = positions.get(enemy.id)!.position; group.position.set(position.x, (this.heights.get(enemy.id) ?? 2) * .9, position.z);
      group.rotation.set(.35, time * .45, Math.PI / 2);
      shade(group, time, (.15 + .15 * Math.pow(Math.max(0, Math.sin(time * 2.5)), 5)) * Math.min(1, remaining * 2));
    }
    for (const [id, group] of this.taunts) if (!taunted.has(id)) { dispose(group); this.taunts.delete(id); }
  }
  clear() {
    for (const flight of this.flights.values()) { dispose(flight.body); flight.wake.dispose(); }
    for (const wake of this.approach.values()) wake.dispose(); for (const item of this.bursts) dispose(item.group); for (const group of this.taunts.values()) dispose(group);
    this.flights.clear(); this.approach.clear(); this.origins.clear(); this.taunts.clear(); this.bursts = []; this.pending = []; this.lights.length = 0;
    this.previewShots.clear(); this.previewTaunts.clear(); this.previewId = -1;
  }
  dispose() { this.clear(); dispose(this.warmup); }
}
